import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type {
  GeofenceLookupQuery,
  GeofenceLookupRepository,
} from "../../../contracts/geofenceOptions";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface GeofenceOption {
  readonly value: string;
  readonly label: string;
}

export interface UseGeofenceOptionsResult {
  readonly options: readonly GeofenceOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated geofence options for the `Combobox` async mode --
 * mirrors useBranchOptions. A client user's list is their own company's; staff
 * pass the company they picked (`accountId`).
 */
export function useGeofenceOptions(
  repository: GeofenceLookupRepository | undefined,
  accountId?: string,
  { enabled = true }: { readonly enabled?: boolean } = {},
): UseGeofenceOptionsResult {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search = debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const listQuery = (page: number): GeofenceLookupQuery => ({
    page,
    limit: PAGE_SIZE,
    ...(search === undefined ? {} : { search }),
    ...(accountId === undefined ? {} : { accountId }),
  });

  const query = useInfiniteQuery({
    queryKey: ["geofences", "options", { limit: PAGE_SIZE, search, accountId }],
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: enabled && repository !== undefined,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((geofence) => ({ value: geofence.id, label: geofence.name })),
      ),
    [query.data],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
